import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Youtube, ExternalLink, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const CATS = [
  { id: "league_match", label: "League Match" },
  { id: "braggers", label: "Bragger's Cup" },
  { id: "tournament", label: "Tournament" },
  { id: "challenge", label: "Challenge Match" },
  { id: "special", label: "Special Event" },
];

export function VideosManager() {
  const [videos, setVideos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ youtube_url: "", title: "", category: "league_match" });

  const load = () => api.get("/admin/videos").then((r) => setVideos(r.data)).catch(() => setVideos([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.youtube_url.trim() || !draft.title.trim()) { toast.error("YouTube URL and title are required."); return; }
    setBusy(true);
    try { await api.post("/admin/videos", draft); toast.success("Video added"); setDraft({ youtube_url: "", title: "", category: "league_match" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add video"); }
    finally { setBusy(false); }
  };

  const save = async (v) => {
    setBusy(true);
    try { await api.put(`/admin/videos/${v.id}`, v); toast.success("Saved"); load(); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/admin/videos/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); }
  };

  const upd = (id, k, val) => setVideos((vs) => vs.map((v) => (v.id === id ? { ...v, [k]: val } : v)));

  if (videos === null) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E63C4]" /></div>;

  return (
    <div className="space-y-6" data-testid="videos-manager">
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-5">
        <p className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Youtube className="h-5 w-5 text-[#E11D2A]" /> Add a video</p>
        <p className="text-sm text-[#777] mb-4">Paste any YouTube link — we extract the video ID automatically. Set the category and details, then add.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <input className={input} placeholder="YouTube URL (watch, youtu.be, live…)" value={draft.youtube_url} onChange={(e) => setDraft({ ...draft, youtube_url: e.target.value })} data-testid="video-add-url" />
          <input className={input} placeholder="Title (e.g. Chalk Therapy vs The Tragically Tip)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="video-add-title" />
          <select className={input} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="video-add-category">
            {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <button onClick={add} disabled={busy} data-testid="video-add-btn" className="inline-flex items-center justify-center gap-2 bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60"><Plus className="h-4 w-4" /> Add video</button>
        </div>
      </div>

      <div className="space-y-4">
        {videos.length === 0 && <p className="text-sm text-[#777]">No videos yet. Add one above — the library also auto-seeds from your YouTube channel on startup.</p>}
        {videos.map((v) => (
          <div key={v.id} className="bg-white rounded-lg border border-[#E5E7EB] p-4 relative" data-testid={`video-row-${v.id}`}>
            <button onClick={() => remove(v.id)} className="absolute top-3 right-3 text-red-500 hover:text-red-700" data-testid={`video-delete-${v.id}`}><Trash2 className="h-4 w-4" /></button>
            <div className="flex gap-3 items-start">
              {v.thumbnail && <img src={v.thumbnail} alt="" className="w-32 aspect-video object-cover rounded border border-[#E5E7EB] shrink-0" />}
              <div className="flex-1 min-w-0 space-y-2">
                <input className={input} value={v.title} onChange={(e) => upd(v.id, "title", e.target.value)} data-testid={`video-title-${v.id}`} />
                <div className="grid sm:grid-cols-3 gap-2">
                  <input className={input} placeholder="YouTube URL" value={v.youtube_url || ""} onChange={(e) => upd(v.id, "youtube_url", e.target.value)} />
                  <select className={input} value={v.category} onChange={(e) => upd(v.id, "category", e.target.value)}>{CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
                  <input className={input} type="date" value={(v.date || "").slice(0, 10)} onChange={(e) => upd(v.id, "date", e.target.value)} />
                </div>
                <p className="text-xs text-[#777]">Slug: <a href={`/videos/${v.slug}`} target="_blank" rel="noreferrer" className="text-[#1E63C4] hover:underline">/videos/{v.slug}</a> <ExternalLink className="inline h-3 w-3" /></p>
                <input className={input} placeholder="Teams / players (e.g. Hot Shots vs Rack Em Up)" value={v.teams || ""} onChange={(e) => upd(v.id, "teams", e.target.value)} />
                <textarea className={input} rows={2} placeholder="Description" value={v.description || ""} onChange={(e) => upd(v.id, "description", e.target.value)} />
                <div className="grid sm:grid-cols-3 gap-2">
                  <input className={input} placeholder="Related Bragger's slug" value={v.related_bragger_slug || ""} onChange={(e) => upd(v.id, "related_bragger_slug", e.target.value)} />
                  <input className={input} placeholder="Related tournament slug" value={v.related_tournament_slug || ""} onChange={(e) => upd(v.id, "related_tournament_slug", e.target.value)} />
                  <input className={input} placeholder="Related division slug" value={v.related_division_slug || ""} onChange={(e) => upd(v.id, "related_division_slug", e.target.value)} />
                </div>
                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.published !== false} onChange={(e) => upd(v.id, "published", e.target.checked)} className="h-4 w-4 accent-[#1E63C4]" data-testid={`video-published-${v.id}`} /> Published</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v.is_live} onChange={(e) => upd(v.id, "is_live", e.target.checked)} className="h-4 w-4 accent-[#E11D2A]" /> Live broadcast</label>
                  <button onClick={() => save(v)} disabled={busy} data-testid={`video-save-${v.id}`} className="ml-auto inline-flex items-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#174e9c] disabled:opacity-60"><Save className="h-4 w-4" /> Save</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
